import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import React from "react";
import { createRoot } from "react-dom/client";

import MortgageExtension from "./components/MortgageExtension";
import i18n, { AVAILABLE_LOCALES, SWISS_GERMAN_LOCALE } from "./translations/i18n";

const HTML_TAG = "mortgage-extension";
const LOCALE_ATTR = "locale";
const DEFAULT_LOCALE = SWISS_GERMAN_LOCALE;

/**
 * Web component definition and binding to React application.
 */
class MortgageExtensionWebComponent extends HTMLElement {
    constructor() {
        super();
        this.renderReactApp();
    }

    /**
     * Renders react app wrapping it inside shadow dom.
     */
    renderReactApp() {
        console.debug("whvo: Bootstrapping mortgage extension started...");

        const shadowRoot = this.attachShadow({ mode: "open" });
        const stylesMountPoint = document.createElement("style");
        stylesMountPoint.id = "whvo-styles-container";
        const domMountPoint = document.createElement("section");
        domMountPoint.id = "whvo-app-container";
        shadowRoot.appendChild(stylesMountPoint);
        const reactContainer = shadowRoot.appendChild(domMountPoint);

        // insertion point for MUI 5 emotion
        const cache = createCache({
            key: "whvo",
            prepend: true,
            container: stylesMountPoint,
        });
        const reactRoot = createRoot(reactContainer!);

        reactRoot.render(
            <CacheProvider value={cache}>
                <MortgageExtension locale={this.getLocale()} customElement={this} shadowRoot={reactContainer} />
            </CacheProvider>
        );

        console.debug("whvo: Bootstrapping mortgage extension app done.");
    }

    /**
     * Retrieves locale from the custom element or a default value.
     */
    getLocale(): string {
        let locale = this.getAttribute(LOCALE_ATTR) ?? DEFAULT_LOCALE;
        if (AVAILABLE_LOCALES.includes(locale)) {
            return locale;
        }
        return DEFAULT_LOCALE;
    }

    /**
     * Custom element attributes whose changes are watched are registered here.
     * @see attributeChangedCallback
     */
    static get observedAttributes() {
        return [LOCALE_ATTR];
    }

    /**
     * A callback which gets triggered whenever some observed attribute gets changed.
     * @see observedAttributes
     */
    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (newValue !== oldValue) {
            if (LOCALE_ATTR === name && AVAILABLE_LOCALES.includes(newValue)) {
                this.changeLocale(oldValue, newValue);
            }
        }
    }

    changeLocale(oldLocale: string, newLocale: string) {
        console.debug(`whvo: mortgage extension app's locale changed from '${oldLocale}' to '${newLocale}'`);

        // for some reason, the i18n needs timeout when the component is created dynamically, otherwise it does not always change the language
        setTimeout(() => {
            i18n.changeLanguage(newLocale);
        }, 100);
    }
}

/**
 * Register our custom element on the page.
 */
const customElementExists = !!customElements.get(HTML_TAG);
if (!customElementExists) {
    console.debug(`whvo: Mortgage extension custom element '${HTML_TAG}' registered on the page.`);
    customElements.define(HTML_TAG, MortgageExtensionWebComponent);
}
